import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, PasswordInput, TextInput } from '@l2u/ui/components/Field.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { parseAuthErrorFromUrl } from '@l2u/ui/lib/authErrorFromUrl.ts';
import { AuthSplitLayout, AuthInfoChip } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { getAdminSupabase } from '@l2u/ui/lib/supabase.ts';
import { createCentralSignIn } from '../lib/auth/centralSignIn.ts';
import { beginSsoAutoStart, safeStorage } from '../lib/auth/ssoHint.ts';
import { readSessionEndReason } from '../lib/auth/sessionTimeout.ts';

// Build-time constant. Since admin sign-in moved to l2u-identity this is only a
// feature flag for the SSO button: the domain VALUE is no longer sent anywhere
// from here, because the central login page decides SSO-versus-magic-link from
// the email it collects. Unset ⇒ no SSO button and the password form shows
// directly (pre-SSO behaviour).
//
// admin/Users.tsx still reads it for its actual value (invite handling), so do
// not repurpose the variable without checking that too.
const SSO_DOMAIN = import.meta.env.VITE_ADMIN_SSO_DOMAIN;

export function AdminLogin() {
  const { user, accessDenied, signInWithPassword } = useAdminAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const endReason = readSessionEndReason(params.get('reason'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(!SSO_DOMAIN);
  const [ssoSubmitting, setSsoSubmitting] = useState(false);
  const [ssoNotice, setSsoNotice] = useState<string | null>(null);
  // True only while an AUTOMATIC redirect is in flight. A click already
  // carried the user's intent, so it does not raise the interstitial.
  const [autoStarting, setAutoStarting] = useState(false);

  // Captured during RENDER, before any effect can rewrite the URL. The
  // expired-link effect below calls history.replaceState to strip auth errors
  // from the address bar, so reading window.location.search inside an effect
  // could see a cleaned URL and auto-redirect straight back into the failure the
  // error guard exists to prevent.
  const urlAtLoad = useRef({
    search: typeof window === 'undefined' ? '' : window.location.search,
    hash: typeof window === 'undefined' ? '' : window.location.hash,
  });
  const autoStartRef = useRef(false);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Hands-free re-entry for someone who has signed in through central identity
  // on this browser before. Every guard lives in lib/auth/ssoHint.ts and is
  // tested there without a DOM; this stays pure wiring.
  useEffect(() => {
    // StrictMode invokes effects twice in development. The attempt marker inside
    // beginSsoAutoStart would also catch it, but only after a first navigation
    // has already been started.
    if (autoStartRef.current) return;
    autoStartRef.current = true;

    // Already signed in, or authenticated-but-refused: both are handled by the
    // effects below (redirect home, or to /no-access). Starting SSO here would
    // be a pointless round trip for the first and would replace the refusal
    // explanation with one for the second.
    if (user || accessDenied) return;

    const start = beginSsoAutoStart({
      ssoConfigured: Boolean(SSO_DOMAIN),
      search: urlAtLoad.current.search,
      hash: urlAtLoad.current.hash,
      local: safeStorage(() => window.localStorage),
      session: safeStorage(() => window.sessionStorage),
    });
    if (start) {
      setAutoStarting(true);
      void onSsoClick();
    }
  }, []);

  // Refused, and the session is deliberately kept alive (lib/auth/admin.tsx),
  // so this page would otherwise offer a sign-in that returns the same identity
  // and refuses again. Send them to the screen that explains why instead.
  useEffect(() => {
    if (accessDenied) {
      navigate('/no-access', { replace: true, state: { reason: accessDenied.reason } });
    }
  }, [accessDenied, navigate]);

  // Invite/recovery emails sometimes land here (directly or via the legacy
  // /admin/* redirect) when Supabase falls back to its Site URL (e.g. the
  // redirectTo we asked for wasn't on the allow-list).
  // Forward to the right credential-redeeming page based on the link's type.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    if (!hash && !search) return;

    const fragment = hash ? new URLSearchParams(hash.replace(/^#/, '')) : null;
    const query = search ? new URLSearchParams(search) : null;
    const type = (fragment?.get('type') ?? query?.get('type') ?? '').toLowerCase();
    const target = type === 'recovery' ? '/reset-password' : '/accept-invite';

    if (fragment?.get('access_token') && fragment.get('refresh_token')) {
      navigate(`${target}${search}${hash}`, { replace: true });
      return;
    }
    if (query?.get('code') || query?.get('token_hash')) {
      navigate(`${target}${search}${hash}`, { replace: true });
    }
  }, [navigate]);

  // Expired/used links don't carry a token — Supabase appends an error instead
  // (e.g. #error=access_denied&error_code=otp_expired). Surface a friendly
  // notice and strip the error from the URL so a refresh doesn't re-show it.
  useEffect(() => {
    const authError = parseAuthErrorFromUrl(window.location.search, window.location.hash);
    if (!authError) return;
    const message =
      authError.code === 'otp_expired'
        ? "That link has expired or was already used. Ask an administrator to send a new invite, or use 'Forgot password'."
        : (authError.description ??
          'That sign-in link could not be used. Please request a new one.');
    setLinkNotice(message);
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  async function onSsoClick() {
    setError(null);
    setSsoNotice(null);
    setSsoSubmitting(true);
    // createCentralSignIn never throws, so every outcome arrives here as a
    // returned error rather than in a catch — the provider-not-registered case
    // (which rejects synchronously), a failed navigation, and a missing Supabase
    // config. The client is passed as a getter rather than resolved here for that
    // last one: getAdminSupabase() throws when VITE_SUPABASE_URL or
    // VITE_SUPABASE_PUBLISHABLE_KEY is unset, and calling it outside the boundary
    // would reject this handler. The button invokes `void onSsoClick()`, so that
    // would surface as an unhandled rejection with the spinner stuck on and the
    // password fallback never offered.
    const startSignIn = createCentralSignIn(
      () => getAdminSupabase().auth,
      (url) => {
        window.location.href = url;
      },
    );
    const { error: ssoErr } = await startSignIn(`${window.location.origin}/sso/callback`);
    if (!ssoErr) return; // navigating away — leave the spinner running

    // Generic message; keep the password fallback reachable.
    setSsoNotice('Single sign-on is unavailable right now. Please sign in another way.');
    setShowPasswordForm(true);
    setSsoSubmitting(false);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  // Shown ONLY for an automatic redirect. The click it replaced used to carry the
  // user's intent; where the identity and Okta sessions outlive the portal one, a
  // shared machine would otherwise sign the next person in as the last. The
  // opt-out is a real link so it causes a full page load: the URL it lands on
  // clears the hint, so the choice sticks beyond this visit.
  if (autoStarting) {
    return (
      <AuthSplitLayout variant="admin">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl text-slate-900">Signing you in</h2>
            <p className="text-sm text-slate-500">Taking you to your organisation account…</p>
          </div>
          <a
            href="/login?sso_auto=off"
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Not you? Sign in another way
          </a>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Admin sign in</h2>
          <p className="text-sm text-slate-500">
            {SSO_DOMAIN
              ? 'Sign in with your organisation account.'
              : 'Use your administrator credentials.'}
          </p>
        </div>

        {endReason && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            {endReason === 'expired'
              ? 'Your session reached its 12-hour limit. Please sign in again.'
              : 'Your session ended. Please sign in again.'}
          </div>
        )}

        {linkNotice && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            {linkNotice}
          </div>
        )}

        {ssoNotice && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            {ssoNotice}
          </div>
        )}

        {SSO_DOMAIN && (
          <div className="flex flex-col gap-3">
            <Btn
              type="button"
              loading={ssoSubmitting}
              onClick={() => void onSsoClick()}
              className="w-full"
              style={{ borderRadius: 10, height: 48 }}
            >
              Sign in with SSO
            </Btn>
            {!showPasswordForm && (
              <button
                type="button"
                onClick={() => setShowPasswordForm(true)}
                className="text-sm font-medium text-brand-primary hover:underline"
              >
                Sign in another way
              </button>
            )}
          </div>
        )}

        {showPasswordForm && (
          <form className="flex flex-col gap-5" onSubmit={onSubmit}>
            <Field label="Email" required>
              <TextInput
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                style={{ borderRadius: 10 }}
              />
            </Field>

            {/* Password label row carries the "Forgot?" link on the right, so it
              is hand-rolled here rather than using Field's stacked label. */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  Password<span className="ml-0.5 text-red-500">*</span>
                </span>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-brand-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                aria-label="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
                style={{ borderRadius: 10 }}
              />
            </div>

            <ErrorMessage error={error} />
            <Btn
              type="submit"
              loading={submitting}
              disabled={!email.trim() || !password}
              className="w-full"
              style={{ borderRadius: 10, height: 48 }}
            >
              Sign in
            </Btn>
            {/*
              Both limits stated here are real, and neither is enforced in this
              file. Their source of truth is Auth > Sessions on the project
              VITE_SUPABASE_URL points at -- this portal's own, NOT the identity
              project, because that is where every token refresh goes. Confirmed
              there on 2026-09-07: 12-hour timebox, 4-hour inactivity. Change
              them there and here together; ABSOLUTE_MS in
              lib/auth/sessionTimeout.ts mirrors the 12 hours client-side.

              This chip renders only inside the password form, so its audience is
              password admins -- the users for whom a lapse means retyping a
              password, since the SSO re-entry hint is never written for them.

              Deliberately "away" rather than "inactive": the window is measured
              against the SDK's token refresh, and the SDK only refreshes while
              the tab is VISIBLE. So it elapses while the browser is closed,
              asleep, minimised or sitting behind another tab -- but never while
              a tab is on screen, however idle the person in front of it.
            */}
            <AuthInfoChip tone="neutral">
              For security, sessions are limited to 12 hours, and you&apos;ll need to sign in again
              after 4 hours away.
            </AuthInfoChip>
          </form>
        )}
      </div>
    </AuthSplitLayout>
  );
}
